import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiUpload,
  FiTrash2,
  FiFileText,
  FiFilter,
  FiX,
  FiRefreshCw,
  FiInfo,
  FiPlay
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminAPI, subjectsAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const PAPER_TYPES = [
  { value: 'general', label: 'General / Single paper' },
  { value: 'paper1', label: 'Paper 1' },
  { value: 'paper2', label: 'Paper 2' },
  { value: 'paper3', label: 'Paper 3' },
  { value: 'practical', label: 'Practical' }
];

const PastQuestions = () => {
  const [items, setItems] = useState([]);
  const [syllabi, setSyllabi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [filters, setFilters] = useState({
    subject: '',
    examType: '',
    grade: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [form, setForm] = useState({
    file: null,
    syllabus: '',
    subject: '',
    examType: '',
    grade: '',
    paperType: 'general',
    year: '',
    title: ''
  });
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [examTypeOptions, setExamTypeOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [syllabiLoading, setSyllabiLoading] = useState(false);
  const pollRef = useRef(null);
  const { showError, showSuccess } = useNotification();

  const hasProcessing = items.some((item) => (item.extraction?.status || 'pending') === 'processing');

  const fetchSyllabi = useCallback(async () => {
    if (!form.subject) {
      setSyllabi([]);
      return;
    }
    setSyllabiLoading(true);
    try {
      const params = { subject: form.subject };
      if (form.examType) params.examType = form.examType;
      if (form.grade) params.grade = form.grade;
      const res = await adminAPI.getProcessedSyllabiForPastQuestions(params);
      setSyllabi(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load syllabi:', err);
      showError('Failed to load processed syllabi');
      setSyllabi([]);
    } finally {
      setSyllabiLoading(false);
    }
  }, [form.subject, form.examType, form.grade, showError]);

  const prevItemsRef = useRef([]);

  const fetchItems = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (filters.subject) params.subject = filters.subject;
      if (filters.examType) params.examType = filters.examType;
      if (filters.grade) params.grade = filters.grade;
      const res = await adminAPI.getPastQuestions(params);
      const nextItems = res.data?.data || [];
      if (silent && nextItems.length > 0) {
        const prev = prevItemsRef.current;
        const merged = nextItems.map((n) => {
          const cur = prev.find((p) => p._id === n._id);
          const serverStatus = n.extraction?.status ?? 'pending';
          if (cur?.extraction?.status === 'processing' && serverStatus === 'pending') {
            return { ...n, extraction: { ...(cur.extraction || {}), status: 'processing', progressPercent: cur.extraction?.progressPercent ?? 0, progressMessage: cur.extraction?.progressMessage || 'Extracting text from PDF…' } };
          }
          return n;
        });
        setItems(merged);
        setPagination((p) => ({ ...p, total: res.data?.pagination?.totalItems ?? 0, totalPages: res.data?.pagination?.totalPages ?? 1 }));
        return merged;
      }
      setItems(nextItems);
      setPagination(prev => ({
        ...prev,
        total: res.data?.pagination?.totalItems ?? 0,
        totalPages: res.data?.pagination?.totalPages ?? 1
      }));
      return nextItems;
    } catch (err) {
      if (!silent) showError('Failed to load past questions');
      setItems([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters.subject, filters.examType, filters.grade, showError]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    prevItemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (!hasProcessing) return;
    const tick = async () => {
      const next = await fetchItems(true);
      const prev = prevItemsRef.current;
      next.forEach((item) => {
        const prevItem = prev.find((p) => p._id === item._id);
        const prevStatus = prevItem?.extraction?.status ?? 'pending';
        const nowStatus = item.extraction?.status ?? 'pending';
        if (prevStatus === 'processing' && nowStatus === 'completed') {
          showSuccess('Past question ready for use');
        }
      });
      prevItemsRef.current = next;
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasProcessing, fetchItems, showSuccess]);

  useEffect(() => {
    const loadSettingsOptions = async () => {
      try {
        let examTypes = [];
        let classes = [];
        let subjectNames = [];
        const res = await subjectsAPI.getOptions();
        const data = res.data?.data || {};
        examTypes = data.examTypes || [];
        classes = data.classes || [];
        const byExam = data.subjects || {};
        subjectNames = [...new Set(Object.values(byExam).flat())].filter(Boolean).sort();
        if (examTypes.length === 0 && classes.length === 0 && subjectNames.length === 0) {
          const fallback = await adminAPI.getSubjects();
          const list = (fallback.data?.data || []).filter(s => s.isActive !== false);
          if (list.length > 0) {
            examTypes = [...new Set(list.map(s => s.examType))].filter(Boolean).sort();
            classes = [...new Set(list.map(s => s.class))].filter(Boolean).sort();
            subjectNames = [...new Set(list.map(s => s.name))].filter(Boolean).sort();
          }
        }
        setExamTypeOptions(examTypes.map(et => ({ value: et, label: et })));
        setClassOptions(classes.map(c => ({ value: c, label: c })));
        setSubjectOptions(subjectNames.map(name => ({ value: name, label: name })));
      } catch {
        try {
          const fallback = await adminAPI.getSubjects();
          const list = (fallback.data?.data || []).filter(s => s.isActive !== false);
          if (list.length > 0) {
            const et = [...new Set(list.map(s => s.examType))].filter(Boolean).sort();
            const cl = [...new Set(list.map(s => s.class))].filter(Boolean).sort();
            const names = [...new Set(list.map(s => s.name))].filter(Boolean).sort();
            setExamTypeOptions(et.map(e => ({ value: e, label: e })));
            setClassOptions(cl.map(c => ({ value: c, label: c })));
            setSubjectOptions(names.map(name => ({ value: name, label: name })));
          } else {
            setSubjectOptions([]);
            setExamTypeOptions([]);
            setClassOptions([]);
          }
        } catch {
          setSubjectOptions([]);
          setExamTypeOptions([]);
          setClassOptions([]);
        }
      }
    };
    loadSettingsOptions();
  }, []);

  useEffect(() => {
    if (showUpload && form.subject) {
      fetchSyllabi();
    } else if (!form.subject) {
      setSyllabi([]);
    }
  }, [showUpload, form.subject, form.examType, form.grade, fetchSyllabi]);

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!form.file) {
      showError('Please select a PDF file');
      return;
    }
    if (!form.syllabus) {
      showError('Please select a processed syllabus');
      return;
    }
    if (!form.subject || !form.examType || !form.grade) {
      showError('Subject, exam type and grade are required');
      return;
    }

    const fd = new FormData();
    fd.append('file', form.file);
    fd.append('syllabus', form.syllabus);
    fd.append('subject', form.subject);
    fd.append('examType', form.examType);
    fd.append('grade', form.grade);
    fd.append('paperType', form.paperType);
    if (form.year) fd.append('year', form.year);
    if (form.title) fd.append('title', form.title);

    try {
      setUploading(true);
      await adminAPI.uploadPastQuestion(fd);
      showSuccess('Past question uploaded successfully');
      setForm({
        file: null,
        syllabus: '',
        subject: '',
        examType: '',
        grade: '',
        paperType: 'general',
        year: '',
        title: ''
      });
      setShowUpload(false);
      fetchItems();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      showError(msg || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this past question upload? The file will be removed.')) return;
    try {
      await adminAPI.deletePastQuestion(id);
      showSuccess('Past question deleted');
      fetchItems();
    } catch (err) {
      showError(err.response?.data?.message || 'Delete failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const paperLabel = (v) => PAPER_TYPES.find(p => p.value === v)?.label || v;

  const extractionStatus = (item) => item.extraction?.status ?? 'pending';

  const handleRetryExtraction = async (id) => {
    try {
      await adminAPI.triggerPastQuestionExtraction(id);
      showSuccess('Extraction started');
      setItems((prev) =>
        prev.map((it) =>
          it._id === id
            ? {
                ...it,
                extraction: {
                  ...it.extraction,
                  status: 'processing',
                  progressPercent: 0,
                  progressMessage: 'Extracting text from PDF…'
                }
              }
            : it
        )
      );
      fetchItems(true);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to start extraction');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Past Questions</h1>
          <p className="text-gray-600 text-sm">
            Upload past exam papers (PDF) and link them to a processed syllabus. Expert Intelligence uses these to learn question style and structure (Paper 1, 2, 3, Practicals).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowUpload(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm shrink-0"
        >
          <FiUpload className="h-4 w-4" />
          Upload past question
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-blue-100 bg-blue-50/80 px-4 py-3 text-blue-800 text-sm flex items-start gap-3">
        <FiInfo className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium mb-1">How past questions are used</p>
          <p className="text-blue-700">
            After upload, text is extracted from each PDF in the background. When status is <strong>Ready</strong>, that past paper is used automatically when you <strong>Generate questions</strong> from the linked syllabus (Syllabus Management → Generate questions). Expert Intelligence mimics the style and format of your past papers so generated exams feel like the real thing. Paper type (Paper 1, 2, 3, Practical) helps Expert Intelligence match structure.
          </p>
        </div>
      </div>

      {showUpload && (
        <div className="mb-8 rounded-xl border border-gray-200 bg-gray-50 p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Upload past question PDF</h2>
            <button
              type="button"
              onClick={() => setShowUpload(false)}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              aria-label="Close"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleUploadSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PDF file *</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                  className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 file:text-sm file:font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Processed syllabus *</label>
                <select
                  value={form.syllabus}
                  onChange={(e) => setForm(prev => ({ ...prev, syllabus: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 disabled:opacity-60"
                  required
                  disabled={!form.subject || syllabiLoading}
                >
                  <option value="">
                    {!form.subject
                      ? 'Select subject first'
                      : syllabiLoading
                        ? 'Loading…'
                        : 'Select syllabus (Expert Intelligence processed)'}
                  </option>
                  {!syllabiLoading && form.subject && syllabi.length === 0 && (
                    <option value="" disabled>No processed syllabi for this subject. Process one in Syllabus Management.</option>
                  )}
                  {syllabi.map(s => (
                    <option key={s._id} value={s._id}>
                      {s.title} — {s.subject} · {s.examType} · {s.grade}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <select
                  value={form.subject}
                  onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value, syllabus: '' }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  required
                >
                  <option value="">Select subject</option>
                  {subjectOptions.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exam type *</label>
                <select
                  value={form.examType}
                  onChange={(e) => setForm(prev => ({ ...prev, examType: e.target.value, syllabus: '' }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  required
                >
                  <option value="">Select exam type</option>
                  {examTypeOptions.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade *</label>
                <select
                  value={form.grade}
                  onChange={(e) => setForm(prev => ({ ...prev, grade: e.target.value, syllabus: '' }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  required
                >
                  <option value="">Select grade</option>
                  {classOptions.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paper type</label>
                <select
                  value={form.paperType}
                  onChange={(e) => setForm(prev => ({ ...prev, paperType: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                >
                  {PAPER_TYPES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year (optional)</label>
                <input
                  type="text"
                  value={form.year}
                  onChange={(e) => setForm(prev => ({ ...prev, year: e.target.value }))}
                  placeholder="e.g. 2023"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. WAEC Physics Paper 2 2023"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={uploading}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {uploading ? <FiRefreshCw className="h-4 w-4 animate-spin" /> : <FiUpload className="h-4 w-4" />}
                Upload
              </button>
              <button
                type="button"
                onClick={() => setShowUpload(false)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <FiFilter className="h-4 w-4 text-slate-500" />
          <select
            value={filters.subject}
            onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value }))}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
          >
            <option value="">All subjects</option>
            {subjectOptions.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            value={filters.examType}
            onChange={(e) => setFilters(prev => ({ ...prev, examType: e.target.value }))}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
          >
            <option value="">All exam types</option>
            {examTypeOptions.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select
            value={filters.grade}
            onChange={(e) => setFilters(prev => ({ ...prev, grade: e.target.value }))}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
          >
            <option value="">All grades</option>
            {classOptions.map(g => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="large" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FiFileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No past questions yet</p>
            <p className="text-sm">Upload past exam PDFs and link them to a processed syllabus so Expert Intelligence can learn question styles.</p>
            <button
              type="button"
              onClick={() => setShowUpload(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
            >
              <FiUpload className="h-4 w-4" />
              Upload past question
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject / Exam / Grade</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Syllabus</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Paper</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">File</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Uploaded</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {items.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{item.subject}</div>
                      <div className="text-xs text-gray-500">{item.examType} · {item.grade}{item.year ? ` · ${item.year}` : ''}</div>
                      {item.title && <div className="text-xs text-gray-600 mt-0.5">{item.title}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.syllabus?.title ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {paperLabel(item.paperType)}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const status = extractionStatus(item);
                        const percent = Math.min(100, Math.max(0, Number(item.extraction?.progressPercent) || 0));
                        return (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              {status === 'completed' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Ready</span>
                              )}
                              {status === 'pending' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">Pending</span>
                              )}
                              {status === 'processing' && (
                                <>
                                  <FiRefreshCw className="h-4 w-4 flex-shrink-0 animate-spin text-blue-600" aria-hidden />
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Extracting…</span>
                                  <span className="text-xs font-medium text-gray-600">{percent}%</span>
                                </>
                              )}
                              {status === 'failed' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Failed</span>
                              )}
                            </div>
                            {status === 'processing' && (
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 min-w-[80px] h-1.5 rounded-full bg-gray-200 overflow-hidden max-w-[120px]">
                                  <div
                                    className="h-full bg-blue-600 transition-all duration-500"
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            {status === 'processing' && item.extraction?.progressMessage && (
                              <div className="text-xs text-gray-600 max-w-[160px]" title={item.extraction.progressMessage}>
                                {item.extraction.progressMessage}
                              </div>
                            )}
                            {status === 'failed' && item.extraction?.processingError && (
                              <div className="text-xs text-red-600 max-w-[160px]" title={item.extraction.processingError}>
                                {item.extraction.processingError}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-700 truncate max-w-[180px]" title={item.file?.originalName}>
                        {item.file?.originalName ?? '—'}
                      </div>
                      <div className="text-xs text-gray-500">{formatFileSize(item.file?.size)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(extractionStatus(item) === 'failed' || extractionStatus(item) === 'pending') && (
                          <button
                            type="button"
                            onClick={() => handleRetryExtraction(item._id)}
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                            title={extractionStatus(item) === 'failed' ? 'Retry extraction' : 'Start extraction'}
                          >
                            <FiPlay className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDelete(item._id)}
                          className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && pagination.total > pagination.limit && (
          <div className="px-4 py-3 border-t border-slate-200 flex justify-between items-center text-sm text-slate-600">
            <span>Showing {items.length} of {pagination.total}</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={pagination.page >= (pagination.totalPages || 1)}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastQuestions;
